import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip, { ChipProps } from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import ManageSearchOutlined from '@mui/icons-material/ManageSearchOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import SearchOffOutlined from '@mui/icons-material/SearchOffOutlined';
import Stack from '@mui/material/Stack';
import * as yup from 'yup';
import { DataGrid, GridAlignment, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { FormInput, Loader, theme } from '@omnigenbiodata/ui';

import PortalLayout from '../../../../../../components/layouts/PortalLayout';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import { hasSuccessSelector, isBusySelector, siteSearchThunk, responseSelector } from '../../../../../../store/sites';
import ROUTES from '../../../../../../core/constants/routes.constants';
import PageBanner from '../../../../../../components/layouts/PageBanner';

const primaryTint = theme.palette.grey[100];
const primaryTintStrong = theme.palette.grey[100];
const successTint = theme.palette.success.lighter;

interface SiteSearchValues {
  keyword: string;
}

interface SiteSearchRow {
  code: string;
  name: string;
  address1: string;
  address2: string;
  postcode: string;
  initiated: boolean;
  provider: string;
}

const validationSchema = yup.object().shape({
  keyword: yup.string().trim().min(3, 'Enter at least 3 characters').required('Enter a search term'),
});

function getSiteStatusColour(initiated: boolean | null | undefined): ChipProps['color'] {
  return initiated ? 'success' : 'warning';
}

function renderSiteStatus(initiated: boolean | null | undefined) {
  return (
    <Chip
      label={initiated ? 'INITIATED' : 'UNINITIATED'}
      color={getSiteStatusColour(initiated)}
      size="small"
      sx={{ fontWeight: 700 }}
    />
  );
}

function getColumns(onViewSite: (code: string) => void): GridColDef<SiteSearchRow>[] {
  return [
    {
      field: 'code',
      headerName: 'ODS Code',
      flex: 0.8,
      minWidth: 130,
    },
    {
      field: 'name',
      headerName: 'Site Name',
      flex: 1.4,
      minWidth: 220,
    },
    {
      field: 'address1',
      headerName: 'Address',
      flex: 1.2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<SiteSearchRow, string>) => (
        <Box>
          <Typography variant="body2" component="p" sx={{ fontWeight: 600 }}>
            {params.row.address1 || 'Not recorded'}
          </Typography>

          <Typography variant="caption" component="p" color="text.secondary">
            {[params.row.address2, params.row.postcode].filter(Boolean).join(', ') || 'Not recorded'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'provider',
      headerName: 'Provider',
      flex: 0.8,
      minWidth: 130,
    },
    {
      field: 'initiated',
      headerName: 'Initiated',
      flex: 0.8,
      minWidth: 130,
      align: 'center' as GridAlignment,
      headerAlign: 'center' as GridAlignment,
      renderCell: (params: GridRenderCellParams<SiteSearchRow, boolean>) => renderSiteStatus(params.value),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.5,
      minWidth: 110,
      sortable: false,
      filterable: false,
      align: 'center' as GridAlignment,
      headerAlign: 'center' as GridAlignment,
      renderCell: (params: GridRenderCellParams<SiteSearchRow>) => (
        <Button
          size="small"
          variant="outlined"
          endIcon={<OpenInNewOutlined />}
          onClick={(event: SyntheticEvent) => {
            event.stopPropagation();
            onViewSite(params.row.code);
          }}
        >
          View
        </Button>
      ),
    },
  ];
}

function EmptyResultsPanel({ hasSearched }: { hasSearched: boolean }) {
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        border: 1,
        borderColor: 'rgba(16, 53, 68, 0.12)',
        bgcolor: primaryTint,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 6, bgcolor: 'primary.main' }} />

      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          {hasSearched ? (
            <SearchOffOutlined color="primary" sx={{ fontSize: 30 }} />
          ) : (
            <LocalHospitalOutlined color="primary" sx={{ fontSize: 30 }} />
          )}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
            {hasSearched ? 'No matching sites found' : 'No sites to display'}
          </Typography>

          <Typography variant="body2" component="p" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {hasSearched
              ? 'No enrolment sites matched your search. Try a different site name, ODS code or provider.'
              : 'Enter a site name, ODS code or provider to search for matching enrolment sites.'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

function SitesListScene() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isBusy = useAppSelector(isBusySelector);
  const hasSuccess = useAppSelector(hasSuccessSelector);
  const response = useAppSelector(responseSelector);

  const results = response?.items ?? [];
  const nextToken = response?.nextToken ?? null;

  const [lastKeyword, setLastKeyword] = useState('');

  const handleViewSite = useCallback(
    (code: string) => {
      navigate(ROUTES.portalSite.replace(':code', code));
    },
    [navigate],
  );

  const columns = useMemo(() => getColumns(handleViewSite), [handleViewSite]);

  const formik = useFormik<SiteSearchValues>({
    initialValues: { keyword: '' },
    validationSchema,
    onSubmit: (values) => {
      const keyword = values.keyword.trim();

      setLastKeyword(keyword);

      dispatch(
        siteSearchThunk({
          keyword,
          nextToken: null,
        }),
      );
    },
  });

  const handleLoadMore = () => {
    if (!nextToken || !lastKeyword) {
      return;
    }

    dispatch(
      siteSearchThunk({
        keyword: lastKeyword,
        nextToken,
      }),
    );
  };

  const hasResults = results.length > 0;
  const hasSearched = Boolean(lastKeyword) || hasSuccess;

  return (
    <PortalLayout>
      <Loader label="Loading Sites" isVisible={isBusy} />

      <Box pb={3}>
        <PageBanner
          title="Enrolment Sites"
          description="Search, review and manage enrolment sites, initiation status, site systems and location details."
          extras={
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'rgba(16, 53, 68, 0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: successTint,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                <PlaceOutlined color="primary" sx={{ fontSize: 28 }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" component="p" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  Site administration
                </Typography>

                <Typography variant="body2" component="p" color="text.secondary">
                  Use search to locate an enrolment site.
                </Typography>
              </Box>
            </Box>
          }
        />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                border: 1,
                borderColor: 'rgba(16, 53, 68, 0.18)',
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ height: 6, bgcolor: 'primary.main' }} />

              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  bgcolor: primaryTintStrong,
                  borderBottom: 1,
                  borderColor: 'rgba(16, 53, 68, 0.12)',
                }}
              >
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                      aria-hidden="true"
                    >
                      <ManageSearchOutlined color="primary" sx={{ fontSize: 28 }} />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Search sites
                      </Typography>

                      <Typography variant="body2" component="p" color="text.secondary">
                        Search by site name, ODS code or provider.
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="form" onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} md={9}>
                        <FormInput
                          error={formik.touched.keyword ? formik.errors.keyword : undefined}
                          name="keyword"
                          label="Search term"
                          type="text"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          touched={formik.touched.keyword}
                          value={formik.values.keyword}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3}
                        sx={{
                          pt: { md: '30px !important' },
                        }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          size="large"
                          startIcon={<ManageSearchOutlined />}
                          disabled={isBusy}
                          fullWidth
                          sx={{
                            height: 56,
                            fontWeight: 700,
                          }}
                        >
                          Search
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              </Box>

              <Box p={{ xs: 2, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Search results
                    </Typography>

                    <Typography variant="body2" component="p" color="text.secondary">
                      {hasResults
                        ? `${results.length} site record${results.length === 1 ? '' : 's'} shown`
                        : hasSearched
                          ? 'No matching site records found'
                          : 'No search has been run yet'}
                    </Typography>
                  </Box>

                  {hasResults && (
                    <Chip
                      label={`${results.length} result${results.length === 1 ? '' : 's'}`}
                      size="small"
                      sx={{
                        bgcolor: successTint,
                        color: 'primary.main',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>

                {hasResults ? (
                  <>
                    <Box
                      sx={{
                        width: '100%',
                        border: 1,
                        borderColor: 'rgba(16, 53, 68, 0.12)',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: primaryTint,
                          color: 'primary.main',
                          fontWeight: 700,
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          fontWeight: 700,
                        },
                        '& .MuiDataGrid-row': {
                          cursor: 'pointer',
                        },
                        '& .MuiDataGrid-row:hover': {
                          bgcolor: primaryTint,
                        },
                      }}
                    >
                      <DataGrid
                        loading={isBusy}
                        rows={results}
                        getRowId={(row: SiteSearchRow) => row.code}
                        columns={columns}
                        autoHeight
                        disableRowSelectionOnClick
                        hideFooterPagination
                        hideFooterSelectedRowCount
                        onRowClick={(params) => handleViewSite(String(params.id))}
                        sx={{
                          border: 0,
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        pt: 3,
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={!nextToken || isBusy}
                        onClick={handleLoadMore}
                        endIcon={nextToken ? <ArrowForwardOutlined /> : undefined}
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {nextToken ? 'Load more results' : 'No more results'}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <EmptyResultsPanel hasSearched={hasSearched} />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </PortalLayout>
  );
}

export default SitesListScene;
